import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import {
	Input,
	Radio,
	Checkbox,
	Select,
	DatePicker,
	Spin,
	Notification
} from 'Components';
import { Input as AntInput } from 'antd';
import moment from 'moment';
import LMSButton from 'LearningManager/components/Button';
import SelectCourseModal from 'LearningManager/components/SelectCourseModal';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import {
	Activity,
	ActivityExpirationType,
	ActivityExpirationUnit,
	UpdateActivityPayload
} from 'Services/Api/Activities/Types';
import styles from 'LearningManager/styles/CoursesMapping.module.scss';

const EXPIRATION_UNITS: Exclude<ActivityExpirationUnit, null>[] = [
	'day',
	'week',
	'month',
	'quarter',

	'year'
];

const emptyForm: UpdateActivityPayload = {
	name: '',
	description: '',
	type_id: 0,
	popup_width: 0,
	popup_height: 0,
	is_required: false,
	is_accessible_to_all_tenants: false,
	tenant_ids: [],
	expiration_type: 'none',
	expiration_interval_value: null,
	expiration_interval_unit: null,
	expiration_date: null,
	lms_instance_mappings: []
};

const activityToForm = ({ id, ...payload }: Activity): UpdateActivityPayload =>
	payload;

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
	label,
	children
}) => (
	<div className={styles.field}>
		<div className={styles.field__label}>{label}</div>
		<div className={styles.field__control}>{children}</div>
	</div>
);

const CoursesMapping: React.FC = observer(() => {
	const {
		learningManagerStore,
		userDashboardStore,
		assignUsersStore
	} = useStores();
	const [form, setForm] = useState<UpdateActivityPayload>(emptyForm);
	const [isSubmitting, setIsSubmitting] = useState(false);
	// The activity being edited. Null until a course is picked from the modal;
	// the whole form stays disabled until then.
	const [activityId, setActivityId] = useState<number | null>(null);
	const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
		null
	);
	const [isPickerOpen, setIsPickerOpen] = useState(false);

	const isFormDisabled = activityId === null;

	const lmsInstances = userDashboardStore.lmsInstances;
	const activitiesByInstance =
		learningManagerStore.lmsInstanceActivitiesByInstanceId;
	const organizations = assignUsersStore.organizations;
	const activityTypes = learningManagerStore.activityTypes;
	const isLoadingCourses =
		learningManagerStore.status.listActivities === Status.Pending;
	const isLoadingOrganizations =
		assignUsersStore.status.fetchOrganizations === Status.Pending;

	useEffect(() => {
		userDashboardStore.fetchLmsInstances().catch(() => undefined);
		learningManagerStore
			.fetchLmsInstanceActivities()
			.catch(() => undefined);
		learningManagerStore.fetchActivityTypes().catch(() => undefined);
		assignUsersStore.fetchOrganizations().catch(() => undefined);
	}, [learningManagerStore, userDashboardStore, assignUsersStore]);

	const openCoursePicker = () => {
		setIsPickerOpen(true);
		learningManagerStore
			.listActivities({ offset: 0, page_size: 1000 })
			.catch(() =>
				Notification.error({
					description: 'Failed to load courses. Please try again.'
				})
			);
	};

	// Selecting a course seeds the form from its data (the list already returns
	// the full activity, so no extra fetch is needed).
	const handleSelectCourse = (activity: Activity) => {
		setActivityId(activity.id);
		setSelectedActivity(activity);
		setForm(activityToForm(activity));
		setIsPickerOpen(false);
	};

	const update = <K extends keyof UpdateActivityPayload>(
		key: K,
		value: UpdateActivityPayload[K]
	) => setForm((prev) => ({ ...prev, [key]: value }));

	// "Access to all tenants = yes" means the course is available to everyone, so
	// no specific customers (tenant_ids) are stored; "no" requires picking them.
	const changeAccessToAllTenants = (accessible: boolean) =>
		setForm((prev) => ({
			...prev,
			is_accessible_to_all_tenants: accessible,
			tenant_ids: accessible ? [] : prev.tenant_ids
		}));

	const changeExpirationType = (type: ActivityExpirationType) =>
		setForm((prev) => ({
			...prev,
			expiration_type: type,
			expiration_interval_value:
				type === 'interval'
					? prev.expiration_interval_value ?? 1
					: null,
			expiration_interval_unit:
				type === 'interval'
					? prev.expiration_interval_unit ?? 'year'
					: null,
			expiration_date: type === 'date' ? prev.expiration_date : null
		}));

	const findMapping = (instanceId: number) =>
		form.lms_instance_mappings.find(
			(m) => Number(m.lms_instance_id) === instanceId
		);

	const toggleLmsInstance = (instanceId: number, enabled: boolean) =>
		update(
			'lms_instance_mappings',
			enabled
				? [
						...form.lms_instance_mappings,
						{
							lms_instance_id: String(instanceId),
							lms_activity_id: 0
						}
				  ]
				: form.lms_instance_mappings.filter(
						(m) => Number(m.lms_instance_id) !== instanceId
				  )
		);

	const setLmsActivity = (instanceId: number, lmsActivityId: number) =>
		update(
			'lms_instance_mappings',
			findMapping(instanceId)
				? form.lms_instance_mappings.map((m) =>
						Number(m.lms_instance_id) === instanceId
							? { ...m, lms_activity_id: lmsActivityId }
							: m
				  )
				: [
						...form.lms_instance_mappings,
						{
							lms_instance_id: String(instanceId),
							lms_activity_id: lmsActivityId
						}
				  ]
		);

	const isValid = useMemo(() => {
		if (!form.name.trim()) return false;
		if (!form.type_id) return false;
		if (!form.is_accessible_to_all_tenants && form.tenant_ids.length === 0)
			return false;
		if (form.expiration_type === 'date' && !form.expiration_date)
			return false;
		if (
			form.expiration_type === 'interval' &&
			!form.expiration_interval_value
		)
			return false;
		return true;
	}, [form]);

	const buildPayload = (): UpdateActivityPayload => ({
		...form,
		name: form.name.trim(),
		description: form.description.trim(),
		tenant_ids: form.is_accessible_to_all_tenants ? [] : form.tenant_ids,
		expiration_interval_value:
			form.expiration_type === 'interval'
				? form.expiration_interval_value
				: null,
		expiration_interval_unit:
			form.expiration_type === 'interval'
				? form.expiration_interval_unit ?? 'year'
				: null,
		expiration_date:
			form.expiration_type === 'date' ? form.expiration_date : null,
		lms_instance_mappings: form.lms_instance_mappings.filter(
			(m) => m.lms_activity_id > 0
		)
	});

	const handleSave = async () => {
		if (!activityId) {
			Notification.error({
				description: 'No course selected to update.'
			});
			return;
		}

		setIsSubmitting(true);
		try {
			await learningManagerStore.updateActivity(
				activityId,
				buildPayload()
			);
			Notification.success({
				description: 'Course mapping updated.'
			});
		} catch {
			Notification.error({
				description: 'Something went wrong. Please try again.'
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<div id="lms-course-mapping" className={styles.page}>
			<div className={styles.header}>
				<div className={styles.header__main}>
					<div className={styles.header__title}>Edit Course</div>
					<div className={styles.header__desc}>
						{activityId
							? `Editing “${form.name.trim()}”`
							: 'Select a course to edit its details and external LMS mappings'}
					</div>
				</div>
				<LMSButton
					id="lms-cm-select-course"
					size="small"
					onClick={openCoursePicker}
				>
					{activityId ? 'Change Course' : 'Select Course'}
				</LMSButton>
			</div>

			<div className={styles.card}>
				<section className={styles.section}>
					<Field label="Name">
						<Input
							id="lms-cm-name"
							value={form.name}
							disabled={isFormDisabled}
							onChange={(e) => update('name', e.target.value)}
							placeholder="Enter course name"
						/>
					</Field>

					<Field label="Description">
						<AntInput.TextArea
							id="lms-cm-description"
							className={styles.textarea}
							value={form.description}
							disabled={isFormDisabled}
							onChange={(e) =>
								update('description', e.target.value)
							}
							placeholder="Enter course description"
							autoSize={{ minRows: 3, maxRows: 6 }}
						/>
					</Field>

					<Field label="Course Type">
						<Select
							id="lms-cm-course-type"
							className={styles.typeSelect}
							value={form.type_id || undefined}
							disabled={isFormDisabled}
							showSearch
							optionFilterProp="children"
							placeholder="Select course type"
							onChange={(value) =>
								update('type_id', value as number)
							}
						>
							{activityTypes.map((type) => (
								<Select.Option key={type.id} value={type.id}>
									{type.name}
								</Select.Option>
							))}
						</Select>
					</Field>

					<Field label="Popup Width">
						<div className={styles.numberField}>
							<Input
								id="lms-cm-popup-width"
								type="number"
								min={0}
								className={styles.numberInput}
								value={form.popup_width || ''}
								disabled={isFormDisabled}
								onChange={(e) =>
									update(
										'popup_width',
										Number(e.target.value) || 0
									)
								}
								placeholder="0"
							/>
							<span className={styles.unit}>px</span>
						</div>
					</Field>

					<Field label="Popup Height">
						<div className={styles.numberField}>
							<Input
								id="lms-cm-popup-height"
								type="number"
								min={0}
								className={styles.numberInput}
								value={form.popup_height || ''}
								disabled={isFormDisabled}
								onChange={(e) =>
									update(
										'popup_height',
										Number(e.target.value) || 0
									)
								}
								placeholder="0"
							/>
							<span className={styles.unit}>px</span>
						</div>
					</Field>
				</section>

				<div className={styles.divider} />

				<section className={styles.section}>
					<Field label="Category">
						<Radio.Group
							value={form.is_required ? 'required' : 'optional'}
							disabled={isFormDisabled}
							onChange={(e) =>
								update(
									'is_required',
									e.target.value === 'required'
								)
							}
						>
							<Radio value="required">Required</Radio>
							<Radio value="optional">Optional</Radio>
						</Radio.Group>
					</Field>

					<Field label="Access to All Tenants">
						<Radio.Group
							value={
								form.is_accessible_to_all_tenants ? 'yes' : 'no'
							}
							disabled={isFormDisabled}
							onChange={(e) =>
								changeAccessToAllTenants(
									e.target.value === 'yes'
								)
							}
						>
							<Radio value="yes">Yes</Radio>
							<Radio value="no">No</Radio>
						</Radio.Group>
					</Field>

					{!form.is_accessible_to_all_tenants && (
						<Field label="Customers">
							<div className={styles.checkList}>
								{isLoadingOrganizations ? (
									<div className={styles.checkList__state}>
										<Spin />
									</div>
								) : organizations.length === 0 ? (
									<div className={styles.checkList__state}>
										No organizations available.
									</div>
								) : (
									<Checkbox.Group
										value={form.tenant_ids}
										disabled={isFormDisabled}
										onChange={(values) =>
											update(
												'tenant_ids',
												values as number[]
											)
										}
									>
										{organizations.map((org) => (
											<Checkbox
												key={org.id}
												value={org.id}
											>
												{org.name}
											</Checkbox>
										))}
									</Checkbox.Group>
								)}
							</div>
						</Field>
					)}
				</section>

				<div className={styles.divider} />

				<section className={styles.section}>
					<Field label="Expiration">
						<div className={styles.expiration}>
							<Radio.Group
								value={form.expiration_type}
								disabled={isFormDisabled}
								onChange={(e) =>
									changeExpirationType(e.target.value)
								}
							>
								<Radio value="none">None</Radio>
								<Radio value="interval">Duration</Radio>
								<Radio value="date">Date</Radio>
							</Radio.Group>

							{form.expiration_type === 'interval' && (
								<div className={styles.expiration__inline}>
									<Input
										id="lms-cm-expiration-value"
										type="number"
										min={1}
										className={styles.numberInput}
										value={
											form.expiration_interval_value ?? ''
										}
										disabled={isFormDisabled}
										onChange={(e) =>
											update(
												'expiration_interval_value',
												Number(e.target.value) || null
											)
										}
										placeholder="1"
									/>
									<Select
										id="lms-cm-expiration-unit"
										className={styles.unitSelect}
										value={
											form.expiration_interval_unit ??
											'year'
										}
										disabled={isFormDisabled}
										onChange={(value) =>
											update(
												'expiration_interval_unit',
												value as ActivityExpirationUnit
											)
										}
									>
										{EXPIRATION_UNITS.map((unit) => (
											<Select.Option
												key={unit}
												value={unit}
											>
												{unit.charAt(0).toUpperCase() +
													unit.slice(1)}
												(s)
											</Select.Option>
										))}
									</Select>
								</div>
							)}

							{form.expiration_type === 'date' && (
								<DatePicker
									className={styles.datePicker}
									disabled={isFormDisabled}
									value={
										form.expiration_date
											? moment(
													`${moment().year()}-${
														form.expiration_date
													}`,
													'YYYY-MM-DD'
											  )
											: undefined
									}
									onChange={(date) =>
										update(
											'expiration_date',
											date ? date.format('MM-DD') : null
										)
									}
									placeholder="Select expiration date"
								/>
							)}
						</div>
					</Field>
				</section>

				<div className={styles.divider} />

				<section className={styles.section}>
					<Field label="LMS Mapping">
						<div className={styles.lmsList}>
							{lmsInstances.length === 0 && (
								<div className={styles.lmsEmpty}>
									No LMS instances available.
								</div>
							)}
							{lmsInstances.map((instance) => {
								const mapping = findMapping(instance.id);
								const activities =
									activitiesByInstance[instance.id] ?? [];
								return (
									<div
										key={instance.id}
										className={styles.lmsRow}
									>
										<Checkbox
											className={styles.lmsRow__check}
											checked={Boolean(mapping)}
											disabled={isFormDisabled}
											onChange={(e) =>
												toggleLmsInstance(
													instance.id,
													e.target.checked
												)
											}
										>
											{instance.name}
										</Checkbox>
										{/* Show the course by name, not id. */}
										<Select
											className={styles.lmsSelect}
											value={
												mapping?.lms_activity_id ||
												undefined
											}
											disabled={
												isFormDisabled || !mapping
											}
											showSearch
											optionFilterProp="children"
											placeholder="Select course"
											onChange={(value) =>
												setLmsActivity(
													instance.id,
													value as number
												)
											}
										>
											{activities.map((activity) => (
												<Select.Option
													key={
														activity.lms_activity_id
													}
													value={
														activity.lms_activity_id
													}
												>
													{activity.lms_activity_name}
												</Select.Option>
											))}
										</Select>
									</div>
								);
							})}
						</div>
					</Field>
				</section>
			</div>

			<div className={styles.footer}>
				<LMSButton
					id="lms-cm-cancel"
					variant="border"
					size="small"
					disabled={isFormDisabled || isSubmitting}
					onClick={() =>
						setForm(
							selectedActivity
								? activityToForm(selectedActivity)
								: emptyForm
						)
					}
				>
					Cancel
				</LMSButton>
				<LMSButton
					id="lms-cm-save"
					size="small"
					disabled={isFormDisabled || !isValid || isSubmitting}
					onClick={handleSave}
				>
					Save
				</LMSButton>
			</div>

			<SelectCourseModal
				visible={isPickerOpen}
				activities={learningManagerStore.activities}
				loading={isLoadingCourses}
				selectedId={activityId}
				onSelect={handleSelectCourse}
				onCancel={() => setIsPickerOpen(false)}
			/>
		</div>
	);
});

export default CoursesMapping;
